import { useEffect, useRef } from 'react';

const SOUNDING = 'chord--sounding';
// How long the lit chord rings for, read by the progress rule in the stylesheet
const DRAIN = '--sounding-seconds';
// How much of the line the lit chord is kept clear of the edge by, when it is scrolled to inside
// its own box. A chord is written over the words it is sung on, so arriving flush against an edge
// brings half of what there is to read. In px rather than em: this is measured against the box.
const SCROLL_MARGIN = 24;

/**
 * Brings a node into view inside one scroller, leaving every scroller above it alone. `below` is
 * extra room kept under it.
 */
const scrollWithin = (box, node, below = 0) => {
  const boxRect = box.getBoundingClientRect();
  const rect = node.getBoundingClientRect();

  if (rect.top < boxRect.top) box.scrollTop -= boxRect.top - rect.top + SCROLL_MARGIN;
  else if (rect.bottom + below > boxRect.bottom) box.scrollTop += rect.bottom + below - boxRect.bottom + SCROLL_MARGIN;

  if (rect.left < boxRect.left) box.scrollLeft -= boxRect.left - rect.left + SCROLL_MARGIN;
  else if (rect.right > boxRect.right) box.scrollLeft += rect.right - boxRect.right + SCROLL_MARGIN;
};

/**
 * Lights the chord being played where it is written in a block of words, and drains the rule under
 * it. Shared by the player's Lyrics view and the builder's preview, so a chart being heard reads
 * the same whichever of the two is open.
 *
 * Lit by hand rather than by rendering: the words are laid out once, and moving a class between two
 * nodes is what a chord change costs instead of re-rendering a verse every time one lands. The rule
 * is the same class doing the work — its length goes onto the node as --sounding-seconds and the
 * animation runs on the compositor, so how far a chord has left to run is drawn without React
 * hearing about it between one chord and the next.
 *
 * The nth `.chord` in the element is the nth chord of the chart. Nothing in the markup says so, and
 * nothing has to: where the two are not the same chords there is simply no nth node to light.
 *
 * @param {number} currentChord - Which chord is ringing, as an index into the chart's strikes
 * @param {number} currentChordSeconds - How long it rings for; 0 is nothing playing
 * @param {*} content - Whatever identifies the words currently rendered. The lit node is found
 *   again when this changes, since the old one is no longer in the document.
 * @param {boolean|object} [contain] - Scroll the chord into view inside this element only, rather
 *   than letting the scrollers above it come along. For a block that sits beside something being
 *   worked on — the builder's grid — where a page that jumped to the words would take them away from
 *   it. A ref instead names one box further out that may scroll too: the player's run of parts, whose
 *   list scrolls down while each part's words scroll across.
 * @returns {object} The ref to put on the element holding the words
 */
const useSoundingChord = (currentChord, currentChordSeconds, content, { contain = false } = {}) => {
  const ref = useRef(null);
  const litRef = useRef(null);

  useEffect(() => {
    const lit = currentChordSeconds > 0 ? ref.current?.querySelectorAll('.chord')[currentChord] || null : null;

    if (lit !== litRef.current) {
      litRef.current?.classList.remove(SOUNDING);
      litRef.current?.style.removeProperty(DRAIN);
      litRef.current = lit;
    } else if (lit) {
      // The same chord struck again — a one-chord chart going round on loop. Taking the class off
      // and putting it back is what starts the animation over; leaving it would fill once and stop.
      lit.classList.remove(SOUNDING);
      // Reading a layout property is the flush that makes the browser notice the class went away
      void lit.offsetWidth;
    }

    if (!lit) return;

    lit.style.setProperty(DRAIN, `${currentChordSeconds}s`);
    lit.classList.add(SOUNDING);
    // A verse is taller than the block it is read in, and a line can be wider than it — the words
    // don't wrap here — so the chord being played is brought to the reader on both axes. How it
    // travels, and how much of the line under it comes along, are the stylesheet's (scroll-behavior
    // and the chord's scroll-margin); `auto` here means "however the scroller says".
    if (contain) {
      scrollWithin(ref.current, lit);
      // Down the run, with the room the chord's stylesheet asks for under it — the words it sits over
      if (contain.current) scrollWithin(contain.current, lit, parseFloat(getComputedStyle(lit).scrollMarginBottom) || 0);
    } else {
      lit.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [currentChord, currentChordSeconds, content, contain]);

  // Stops the last chord staying lit on a chart that has been left, or on a view that is closing
  useEffect(
    () => () => {
      litRef.current?.classList.remove(SOUNDING);
      litRef.current?.style.removeProperty(DRAIN);
      litRef.current = null;
    },
    [],
  );

  return ref;
};

export default useSoundingChord;
